/**
 * Keys: a founder's own secrets for any outside service (docs/founder-api.md, "Keys"). The name and note rules mirror
 * the API's (api/src/domain/secrets.ts); the API stays the authority and answers 400 with its own words. Pure.
 */
import type { Secret } from './schemas'

export const KEY_NAME = /^[A-Z][A-Z0-9_]{1,63}$/
export const MAX_NOTE = 200

const RESERVED_EXACT = new Set(['PORT', 'NODE_ENV', 'DATABASE_URL', 'DEV_BYPASS_AUTH'])
const RESERVED_PREFIXES = ['CPG_', 'RAILWAY_']
/** Names a connection owns: set through that connection, never as a key. */
export const CONNECTION_NAMES: ReadonlyMap<string, string> = new Map([
  ['SHOPIFY_STORE_DOMAIN', 'Shopify'],
  ['SHOPIFY_CLIENT_ID', 'Shopify'],
  ['SHOPIFY_CLIENT_SECRET', 'Shopify'],
  ['SHOPIFY_ACCESS_TOKEN', 'Shopify'],
  ['TRIPLEWHALE_API_KEY', 'Triple Whale'],
  ['TRIPLEWHALE_SHOP_DOMAIN', 'Triple Whale'],
])

/** What a founder types -> a name: capitals, with spaces and dashes as underscores ("higgsfield api key" -> HIGGSFIELD_API_KEY). */
export function toKeyName(input: string): string {
  return input.toUpperCase().replace(/[\s-]+/g, '_')
}

/** Why a name can't be used, in plain words, or null when it can. */
export function keyNameProblem(name: string): string | null {
  if (!name) return 'Give it a name.'
  if (!/^[A-Z]/.test(name)) return 'Start with a letter.'
  if (!/^[A-Z0-9_]+$/.test(name)) return 'Use capital letters, digits and underscores only.'
  if (name.length < 2) return 'Use at least two characters.'
  if (name.length > 64) return 'Keep it to 64 characters.'
  if (name.startsWith('VITE_')) return 'Names starting with VITE_ go into the pages every visitor loads, so the key would be public. Drop VITE_.'
  if (RESERVED_EXACT.has(name) || RESERVED_PREFIXES.some((p) => name.startsWith(p))) return `cpg.computer sets ${name} itself. Pick another name.`
  const owner = CONNECTION_NAMES.get(name)
  if (owner) return `${name} comes from your ${owner} connection above.`
  return null
}

/** Kept for callers: every name worth a second thought is now refused by `keyNameProblem`. */
export function keyNameWarning(_name: string): string | null {
  return null
}

/** Why a note can't be saved, or null. Mirrors the API: one line, 200 characters, never the key itself. */
export function keyNoteProblem(note: string, value = ''): string | null {
  const n = note.trim()
  if ([...n].length > MAX_NOTE) return `Keep the note to ${MAX_NOTE} characters.`
  if (/[\u0000-\u001f\u007f]/.test(n)) return 'Keep the note to one line.'
  const v = value.trim()
  if (n && v.length >= 4 && n.includes(v)) return "The note can't hold the key itself. Say what it's for instead."
  return null
}

/** The keys a founder set by hand, by name. A connection's secrets show under that connection instead. */
export function ownKeys(secrets: Secret[]): Secret[] {
  return secrets.filter((s) => !s.connection).sort((a, b) => a.name.localeCompare(b.name))
}

/** How long a fresh key reads as on its way before it reads as waiting. */
export const ON_ITS_WAY_MS = 3 * 60_000

export type Delivery = { state: 'in_place' | 'on_its_way' | 'waiting'; tone: string; label: string; detail: string }

/** Where a key stands: delivered (syncedAt), just set, or waiting for the computer or the app. */
export function delivery(s: Secret, now = Date.now()): Delivery {
  if (s.syncedAt) return { state: 'in_place', tone: 'active', label: 'in place', detail: 'Your app and your computer have it.' }
  if (now - Date.parse(s.setAt) < ON_ITS_WAY_MS) return { state: 'on_its_way', tone: 'running', label: 'on its way', detail: 'Your app redeploys with it in a minute or two.' }
  return { state: 'waiting', tone: 'pending', label: 'waiting', detail: 'It arrives once your computer is on and your app is live.' }
}
